<template>
  <div class="view view-settings">
    <manager-navbar class="view-settings__navbar" />
    <manager-sidebar :is-settings="true" class="view-settings__sidebar">
      <div>
        <div class="manager-sidebar__block pt-0">
          <h4
            class="manager-sidebar__subheading manager-sidebar__icontext mb-3 mt-0"
          >
            <lucide-icon name="account" style="font-size: 19px" />
            <span>{{ $t("headings.accountSettings") }}</span>
          </h4>
          <router-link
            :to="{ name: 'settings-profile' }"
            class="view-settings__navlink"
            draggable="false"
          >
            {{ $t("labels.profile") }}
          </router-link>
          <router-link
            :to="{ name: 'settings-subscriptions' }"
            class="view-settings__navlink"
            draggable="false"
          >
            {{ $t("headings.subscriptions") }}
          </router-link>

          <router-link
            v-if="synchroSettingsVisible"
            :to="{ name: 'settings-synchronization' }"
            draggable="false"
            class="view-settings__navlink"
          >
            {{ $t("labels.synchronization") }}
          </router-link>
        </div>

        <div
          v-if="organizationsArray.length > 0"
          class="manager-sidebar__block view-settings__navhidden"
        >
          <h3
            class="manager-sidebar__subheading manager-sidebar__icontext mb-3"
          >
            <lucide-icon name="organization" class="text-24" />
            <span>{{ $t("headings.organizationsAndWorkspaces") }}</span>
          </h3>

          <router-link
            v-for="organization in visibleOrganizationsWithTeams"
            :key="organization.slug"
            v-slot="{ navigate, isActive, href }"
            :to="{
              name: 'settings-organization',
              params: { organizationSlug: organization.slug }
            }"
            custom
          >
            <a
              :href="href"
              class="view-settings__navlink view-settings__navlink-flex"
              :class="{ 'is-active': isActive }"
              draggable="false"
              @click="navigate"
            >
              <span
                >{{ organization.name }}
                <template v-if="organization.expired"
                  >({{ $t("labels.expired") }})</template
                ></span
              >

              <span
                v-if="
                  organization.membership?.roleCode === 'OWNER' &&
                  organization.membership?.role
                "
                :title="organization.membership?.role?.name"
              >
                ({{ organization.membership?.role?.name }})
              </span>
            </a>

            <div v-if="organization.teams.length > 0">
              <sortable-list
                :items="organization.teams"
                :sortable="
                  organization?.membership?.role?.canManageOrganization ?? false
                "
                @reorder-item="onTeamReorder"
              >
                <template #default="{ item: team }">
                  <router-link
                    class="view-settings__navlink"
                    :to="{
                      name: 'settings-team',
                      params: {
                        organizationSlug: organization.slug,
                        teamSlug: team.slug
                      }
                    }"
                    draggable="false"
                  >
                    <lucide-icon
                      name="subitem"
                      class="view-settings__iconsub mr-2"
                    />
                    <span>{{ team.name }}</span>
                  </router-link>
                </template>
              </sortable-list>
            </div>
          </router-link>
        </div>

        <div
          v-if="user?.isUserAdmin || user?.isGlobalAdmin"
          class="manager-sidebar__block"
        >
          <router-link
            :to="{ name: 'admin-users-list' }"
            class="view-settings__navlink"
          >
            {{ $t("labels.userAdministration") }}
          </router-link>
        </div>
      </div>
    </manager-sidebar>
    <main class="view-settings__main">
      <router-view :key="$route.path"></router-view>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import envOptions from "@/helpers/Env";

import { useUser } from "@/api/user.store";
import { useOrganizations } from "@manager/organizations/organizations.store";
import { useWorkspaces } from "@/api/workspaces.store";

import LucideIcon from "@core/ui/LucideIcon.vue";
import SortableList from "@core/ui/SortableList.vue";
import ManagerNavbar from "../common/ManagerNavbar.vue";
import ManagerSidebar from "../common/ManagerSidebar.vue";
import type { OrganizationMember } from "@/api/Organization";
import type { OrganizationWorkspace } from "@/api/Workspace";

const { user } = useUser();
const { organizations, moveTeam } = useOrganizations({}, { user });
const { teamsByOrganization, userTeams, teams } = useWorkspaces({});

const synchroSettingsVisible = envOptions.synchronizeSaves;

const organizationsArray = computed(() => {
  return Object.values(visibleOrganizationsWithTeams.value);
});
const visibleOrganizationsWithTeams = computed(() => {
  const entries: Record<
    string,
    {
      slug: string;
      teams: OrganizationWorkspace[];
      expired?: boolean;
      name: string;
      membership?: OrganizationMember;
    }
  > = {};

  for (const orgId in organizations) {
    const organization = organizations[orgId];

    let entry = entries[organization.slug];
    if (entry == null) {
      entry = {
        ...organization,
        teams: []
      };
      entries[organization.slug] = entry;
    }

    if (!entry.expired) {
      // add all teams if user can manage teams
      const roles = organization?.roles ?? [];
      const orgRole = roles.find(
        (r) => r.roleCode === organization?.membership?.roleCode
      );
      if (orgRole?.canManageTeams) {
        const teams = teamsByOrganization.value?.[organization.slug] ?? [];
        entry.teams = teams;
      }
    }
  }

  for (const team of userTeams.value) {
    const orgSlug = team.organizationSlug;
    let orgEntry = entries[orgSlug];

    if (orgEntry?.expired) continue;

    if (orgEntry == null) {
      orgEntry = {
        slug: team.organizationSlug,
        name: team.organizationName,
        membership: undefined,
        teams: []
      };
      entries[team.organizationSlug] = orgEntry;
    }

    if (!orgEntry.teams.includes(team)) {
      orgEntry.teams.push(team);
    }
  }

  return entries;
});

function onTeamReorder(orderEvent: { id: number; order: number }) {
  const id = orderEvent.id;
  const order = orderEvent.order;

  const team = teams.value.find((f) => f.id == id);

  if (!team) return;

  moveTeam({
    ...team,
    order
  });
}
</script>

<style lang="scss">
.view-settings {
  display: grid;
  grid-template-rows: min-content minmax(0, 1fr);
  grid-template-columns: 20rem minmax(0, 1fr);
  grid-template-areas:
    "navbar navbar"
    "nav main";

  &__navbar {
    grid-area: navbar;
  }

  &__sidebar {
    height: 100%;
    overflow: auto;
  }

  &__nav {
    grid-area: nav;
    box-shadow: 0 0 20px var(--color-shadow-1);
    overflow: auto;
    background-color: var(--color-background-deep);
  }
  &__navlink {
    display: grid;
    grid-template-columns: min-content minmax(0, 1fr);
    padding: 0.5rem 1rem;
    border-left: 4px solid transparent;
    color: var(--color-text);

    &-flex {
      display: flex;
      justify-content: space-between;
      gap: 0.5rem;
    }
    & > span {
      overflow-wrap: break-word;
    }

    &.is-active {
      border-left-color: var(--color-action);
      color: var(--color-action);
      background-color: var(--color-background);
    }
    &:hover {
      background-color: var(--color-background);
      color: var(--color-action);
    }

    &-nested {
      &:hover,
      &.is-active {
        background-color: transparent;
      }
    }
  }
  &__navnest {
    box-shadow: inset 5px 0 20px rgba(0, 0, 0, 0.05);
    // padding-left: 1rem;
  }

  &__navhidden {
    overflow-x: hidden;
  }

  &__navheader {
    margin-block-end: 0.5rem;
    margin-block-start: 1rem;
    padding-left: 1rem;
    border-left: 4px solid transparent;
  }

  &__main {
    grid-area: main;
    padding: 2rem;
    overflow: auto;
    scrollbar-gutter: stable;
  }

  &__iconsub {
    color: var(--color-text-light);
  }
}
</style>
